import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  ContributionDto,
  ContributionDetailDto,
  ContributionListResponse,
  ContributionListParams,
  CreateContributionRequest,
  RefundRequest,
  ReattributeRequest,
  RedesignateRequest,
  ResolutionChainResponse,
  ResolutionResult,
  ResolveContributorShapeRequest,
  UpdateContributionRequest,
  ValidateDraftContributionRequest,
  ValidateTransactionResponse,
  VoidRowRequest,
  TriageItem,
  FiledReportDto,
  DonorInfoRequestDto,
  RowVersion,
} from '@signalvote/shared';
import { DonorInfoRequestChannel, normalizeTransferQualification } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiResponse, staleVersion } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { ApiError } from '../../utils/apiErrorHandler';
import { useCampaignStore } from '../../stores';
import {
  markConfirmed,
  markPendingReview,
  markVoided,
  markReported,
  markUnreported,
  reconcileSortInboxRowVersion,
} from './sortInboxOptimistic';
import { useRowWrite } from '@signalvote/data';
import { useOptimisticStatusFlip } from './optimisticStatusFlip';
import { scheduleCloseHubRefresh } from './closeHubRefresh';
import { CLIENT_CONFLICT_MARKER } from '../../lib/clientConflict';

/**
 * Kind predicate selecting a Contribution row in the sort-inbox cache — the row
 * identity the optimistic patch and the concurrency-token write-back both key on.
 */
const contributionRow = (contributionId: string) => (item: TriageItem): boolean =>
  item.kind === 'Contribution' && item.contribution.id === contributionId;

/**
 * onSettled invalidations for a status-only contribution flip (confirm /
 * unconfirm / mark-reported / unmark-reported): the row's detail + the
 * contributions list + the earliest-staged-date default + the sort inbox.
 * `contacts.all` and `obligations()` are deliberately omitted — a status flip is
 * aggregate-neutral (it changes no donor limit-total or filing trigger), unlike
 * void/unvoid below.
 */
function invalidateContributionStatusFlip(
  queryClient: QueryClient,
  contributionId: string,
): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.detail(contributionId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.earliestStagedDate() });
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
  // A confirm/void/mark-reported flip changes the filing set, so the close-hub aggregates must
  // refresh: the filing preflight (fee nudge + hard blockers), finance-health (readiness pips), and
  // the close-hub period. These are server-side recomputes, so they're TRAILING-DEBOUNCED — a rapid
  // sweep of check-offs coalesces to a single refresh instead of one per click (the cheap
  // per-row/list invalidations above stay immediate, so the row flip + triage count feel instant).
  scheduleCloseHubRefresh(queryClient);
}

/**
 * onSettled invalidations for a contribution void/unvoid: everything a status
 * flip refreshes PLUS `contacts.all` (excluding/restoring a gift shifts the
 * donor's limit-totals) and `obligations()` (it can arm/cancel a rapid or daily
 * filing obligation — GitHub #81).
 */
function invalidateContributionVoid(
  queryClient: QueryClient,
  contributionId: string,
): void {
  invalidateContributionStatusFlip(queryClient, contributionId);
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
}

// ============================================================================
// Query Hooks
// ============================================================================

function buildContributionListUrl(params: ContributionListParams): string {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
  if (params.contactId) searchParams.set('contactId', params.contactId);
  if (params.electionDesignation) searchParams.set('electionDesignation', params.electionDesignation);
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);
  return `/api/finance/contributions?${searchParams.toString()}`;
}

export function useFinanceContributions(
  params: ContributionListParams,
  options?: { enabled?: boolean },
) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.contributions.list(params),
    queryFn: () =>
      apiJson<ContributionListResponse>(buildContributionListUrl(params), {
        error: 'Failed to fetch contributions',
      }),
    // USI-011 added the opt-in `enabled` gate so consumers (e.g., the
    // orphan-refund search-all panel) can avoid firing the list query
    // until the treasurer actually wants results. Defaults to the
    // pre-USI-011 behavior (authenticated + campaign-selected) so
    // existing call sites are byte-equal.
    enabled:
      authService.isAuthenticated() &&
      !!selectedCampaign &&
      (options?.enabled ?? true),
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateContribution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateContributionRequest) =>
      apiJson<ContributionDto>('/api/finance/contributions', {
        method: 'POST',
        json: data,
        error: (body) => new Error(body.error || 'Failed to create contribution'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.earliestStagedDate() });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
      // The ledger + inbox render from the sort-inbox query; without this a
      // manually created contribution only shows after a page reload.
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
      // GitHub #81 — a $20k rapid-report gift (or a $5k+ combined day) creates a
      // FilingObligation server-side; refresh the obligations bar (in-window banner +
      // rapid/daily strip) so it appears without a page reload.
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
    },
  });
}

// ============================================================================
// Validate Draft Contribution
// ============================================================================

export function useValidateDraftContribution() {
  return useMutation({
    mutationFn: (data: ValidateDraftContributionRequest) =>
      apiJson<ValidateTransactionResponse>('/api/finance/contributions/validate-draft', {
        method: 'POST',
        json: data,
        error: (body) => new Error(body.error || 'Failed to validate draft contribution'),
      }),
  });
}

// ============================================================================
// Resolution Hooks (FIN-019)
// ============================================================================

export function useResolutionChain(contributionId: string | null) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.contributions.resolutions(contributionId ?? ''),
    queryFn: () =>
      apiJson<ResolutionChainResponse>(`/api/finance/contributions/${contributionId}/resolutions`, {
        error: 'Failed to fetch resolution chain',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!contributionId,
  });
}

export function useRefundContribution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contributionId, ...data }: RefundRequest & { contributionId: string }) => {
      const response = await apiResponse(`/api/finance/contributions/${contributionId}/refund`, {
        method: 'POST',
        json: data,
      });
      if (response.status === 409) {
        throw new Error(CLIENT_CONFLICT_MARKER);
      }
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to refund contribution');
      }
      return response.json() as Promise<ResolutionResult>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
      // The write set usually covers health, but a Connect refund that loses the race to
      // its charge.refunded webhook answers success with nothing saved, so its
      // X-SV-Writes is empty.
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.health() });
    },
  });
}

export function useReattributeContribution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contributionId, ...data }: ReattributeRequest & { contributionId: string }) => {
      const response = await apiResponse(`/api/finance/contributions/${contributionId}/reattribute`, {
        method: 'POST',
        json: data,
      });
      if (response.status === 409) {
        throw new Error(CLIENT_CONFLICT_MARKER);
      }
      if (response.status === 422) {
        const error = await response.json().catch(() => ({}));
        throw Object.assign(new Error('GateBlocked'), { gateResult: error.gateResult });
      }
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to reattribute contribution');
      }
      return response.json() as Promise<ResolutionResult>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
    },
  });
}

// ============================================================================
// BSW-014: Staged-page detail panel hooks
// ============================================================================

export function useContributionDetail(contributionId: string | null) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.contributions.detail(contributionId ?? ''),
    queryFn: async () => {
      const response = await apiResponse(`/api/finance/contributions/${contributionId}/detail`);
      // Carry the status: a caller that renders this failure has to tell "no such row" from
      // "the server broke", or it tells the treasurer to hunt for a record that is right there.
      if (!response.ok) {
        throw new ApiError(
          'Failed to fetch contribution detail',
          response.status,
          response.status === 401,
        );
      }
      return response.json() as Promise<ContributionDetailDto>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!contributionId,
  });
}

export class StagedRowUpdateError extends Error {
  code: string;
  status: number;
  /**
   * ADR-081 — present only on a `DeleteSupportsFiledReport` refusal: the already-filed
   * report the row is on. The render boundary uses it to name that report and link to it,
   * so the refusal ends on the amendment path instead of a dead end.
   */
  filedReport: FiledReportDto | null;
  constructor(
    code: string,
    status: number,
    message: string,
    filedReport: FiledReportDto | null = null,
    /** The row's version on a 409 stale write, read by `useRowWrite`. */
    readonly currentVersion: number | null = null,
  ) {
    super(message);
    this.code = code;
    this.status = status;
    this.filedReport = filedReport;
  }
}

export async function throwStagedRowError(response: Response, fallbackMessage: string): Promise<never> {
  const body = await response.json().catch(() => ({}));
  // `filedReport` rides only on the ADR-081 refusal; every other code leaves it null.
  const filedReport: FiledReportDto | null =
    typeof body.filedReport?.id === 'string' && typeof body.filedReport?.name === 'string'
      ? { id: body.filedReport.id, name: body.filedReport.name }
      : null;
  throw new StagedRowUpdateError(
    body.code ?? '', response.status, body.error ?? fallbackMessage, filedReport, staleVersion(body));
}

/** The fields a contribution edit re-sends unchanged, read off the row the edit lands on. */
export type EditableContribution = Pick<
  ContributionDto,
  | 'id'
  | 'concurrencyVersion'
  | 'contactId'
  | 'contributionType'
  | 'electionDesignation'
  | 'paymentMethod'
  | 'notes'
  | 'inKindNature'
  | 'inKindDescription'
  | 'inKindFairMarketValueBasis'
  | 'transferQualification'
>;

/** The fields a contribution edit changes. Every field it leaves out is re-sent as the row has it when the edit runs. */
export type ContributionEdit = Partial<Omit<UpdateContributionRequest, 'concurrencyVersion'>>;

export function useUpdateContribution() {
  const queryClient = useQueryClient();

  return useRowWrite<'Contribution', RowVersion, ContributionEdit, ContributionDto, unknown, EditableContribution>(
    {
      entity: 'Contribution',
      send: async (id, concurrencyVersion, edit, row) => {
        // #711: a date/amount edit carries the recorded transfer relationship rather than sending
        // null (which restores a removed exemption), and a switch to Federal Transfer lands on an
        // explicit answer. The normalizer is the one the entry surfaces and the server use.
        const contributionType = edit.contributionType ?? row.contributionType;
        const request: UpdateContributionRequest = {
          contactId: row.contactId,
          electionDesignation: row.electionDesignation,
          paymentMethod: row.paymentMethod,
          notes: row.notes,
          // On a switch away from in-kind the server clears these whatever is sent.
          inKindNature: row.inKindNature,
          inKindDescription: row.inKindDescription,
          inKindFairMarketValueBasis: row.inKindFairMarketValueBasis,
          amount: null,
          date: null,
          ...edit,
          contributionType,
          transferQualification: normalizeTransferQualification(
            contributionType,
            edit.transferQualification ?? row.transferQualification,
          ),
          concurrencyVersion,
        };
        const response = await apiResponse(`/api/finance/contributions/${id}`, { method: 'PATCH', json: request });
        if (!response.ok) {
          await throwStagedRowError(response, 'Failed to update contribution');
        }
        return response.json() as Promise<ContributionDto>;
      },
    },
    {
      onSuccess: (data, { row }) => {
        reconcileSortInboxRowVersion(queryClient, contributionRow(row.id), data.concurrencyVersion);
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.detail(row.id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.earliestStagedDate() });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
        // GitHub #81 — an amount/date edit changes the rapid trigger / day's gross;
        // refresh the obligations bar so the strip appears/clears without a reload.
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
      },
    },
  );
}

/**
 * Issue #322 — the ledger contributor-shape editor. Atomically sets the shared donor's
 * person-vs-organization shape (kind + name) and the agreeing contribution type, then
 * refetches the sort inbox (the NeedsAction/ResolveContributorShape row clears) and the
 * contacts list (every gift from that donor is corrected at once).
 */
export function useResolveContributorShape() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contributionId,
      ...data
    }: ResolveContributorShapeRequest & { contributionId: string }) => {
      const response = await apiResponse(
        `/api/finance/contributions/${contributionId}/resolve-contributor-shape`,
        { method: 'POST', json: data },
      );
      if (!response.ok) {
        await throwStagedRowError(response, 'Failed to update the donor');
      }
      return response.json() as Promise<ContributionDto>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.earliestStagedDate() });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
    },
  });
}

// Stamp the row "Not reported" (Done/Voided) on click; the aggregates it shifts
// (contacts / obligations) stay server-truth-driven via the onSettled refetch.
// The sort-inbox invalidation (USI-009) also drops the row from the inbox queue
// when the Void fires outside the triage modal (StagedDetailPanel).
export function useVoidContribution() {
  return useOptimisticStatusFlip({
    endpoint: {
      entity: 'Contribution',
      send: async (id, concurrencyVersion) => {
        const response = await apiResponse(`/api/finance/contributions/${id}/void`, {
          method: 'POST',
          json: { concurrencyVersion } satisfies VoidRowRequest,
        });
        if (!response.ok) {
          await throwStagedRowError(response, 'Failed to void contribution');
        }
        return response.json() as Promise<ContributionDto>;
      },
    },
    patch: markVoided,
    invalidateKeys: invalidateContributionVoid,
  });
}

// ADR-016 un-void — restores a Voided contribution to Pending (re-review). The
// inverse of `useVoidContribution`; same POST + body + invalidation set. The
// restore button lives in the cancellation chooser dialog — this hook is its
// data path. Reuses the `StagedRowUpdateError` envelope
// (`StagedRowUpdateNotVoided` → 400, `StagedRowUpdateConcurrencyConflict` → 409).
// Restore instantly (Voided → NeedsAction/Confirm), back in the review queue.
// Same POST + body + invalidation set as the void it reverses.
export function useUnvoidContribution() {
  return useOptimisticStatusFlip({
    endpoint: {
      entity: 'Contribution',
      send: async (id, concurrencyVersion) => {
        const response = await apiResponse(`/api/finance/contributions/${id}/unvoid`, {
          method: 'POST',
          json: { concurrencyVersion } satisfies VoidRowRequest,
        });
        if (!response.ok) {
          await throwStagedRowError(response, 'Failed to restore contribution');
        }
        return response.json() as Promise<ContributionDto>;
      },
    },
    patch: markPendingReview,
    invalidateKeys: invalidateContributionVoid,
  });
}

// ADR-016 single-row Delete — permanently removes a non-Reported contribution
// (a mis-entry that never happened). Distinct from `useVoidContribution` (the
// soft, reversible cancel): Delete returns 204 (no payload) and the concurrency
// token rides as a query param (DELETE carries no body). Reuses the
// `StagedRowUpdateError` envelope — the backend emits `DeleteErrorCodes`
// (`DeleteHasDependentRefunds` → 409, `DeleteMissingConcurrencyVersion` → 400,
// etc.) in the same `{ code, error }` shape.
export function useDeleteContribution() {
  const queryClient = useQueryClient();

  return useRowWrite<'Contribution', RowVersion, object, void>(
    {
      entity: 'Contribution',
      send: async (id, concurrencyVersion) => {
        const response = await apiResponse(
          `/api/finance/contributions/${id}?concurrencyVersion=${concurrencyVersion}`,
          { method: 'DELETE' },
        );
        if (!response.ok) {
          await throwStagedRowError(response, 'Failed to delete contribution');
        }
        // 204 No Content — no payload.
      },
    },
    {
      onSuccess: (_data, { row }) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.detail(row.id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.earliestStagedDate() });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
        // GitHub #81 — deleting the row removes it from the rapid trigger / day's gross,
        // which can cancel its obligation; refresh the obligations bar without a reload.
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
      },
    },
  );
}

// USI-009 (ADR-011 § Structural commitment #7): standalone Pending →
// Confirmed flip path that backs the triage modal's pending-contribution
// body. The endpoint lives at POST /api/finance/contributions/{id}/confirm
// (USI-006); this hook is its first frontend consumer.
//
// Distinct shape from `useVoidContribution`: the confirm endpoint
// returns its own `{ code }` payload from `ContributionConfirmErrorCodes`
// (`ContributionNotFound` → 404, `ConcurrencyConflict` → 409, `NotPending` →
// 400) — NOT the `StagedRowUpdateErrorCodes` family the BSW-014 PATCH +
// void endpoints emit. Surfacing it as a typed error class keeps the
// triage modal's 409 messaging structurally distinct from the BSW-014
// "this row was modified elsewhere" message and avoids muddling the two
// error-code namespaces.
export class ContributionConfirmError extends Error {
  code: string;
  status: number;
  constructor(
    code: string,
    status: number,
    message: string,
    /** The row's version on a 409 stale write, read by `useRowWrite`. */
    readonly currentVersion: number | null = null,
  ) {
    super(message);
    this.name = 'ContributionConfirmError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Ledger uncheck (2026-06-12) — POST /contributions/{id}/unconfirm, the
 * inverse of `useConfirmContribution`. Clicking a Done contribution's ✓ on
 * the All-Time Ledger instantly moves the row one state back
 * (Confirmed → Pending) so the Confirm button returns. Same error envelope
 * as confirm (`ContributionUnconfirmErrorCodes` on the wire).
 */
// Ledger uncheck (2026-06-12) — POST /contributions/{id}/unconfirm, the inverse
// of useConfirmContribution. Clicking a Done contribution's ✓ moves the row one
// state back (Confirmed → Pending) so the Confirm button returns. Status-only
// flip: aggregates untouched, so contacts.all is deliberately omitted.
export function useUnconfirmContribution() {
  return useOptimisticStatusFlip({
    endpoint: {
      entity: 'Contribution',
      send: (id, concurrencyVersion) =>
        apiJson<ContributionDto>(`/api/finance/contributions/${id}/unconfirm`, {
          method: 'POST',
          json: { concurrencyVersion },
          error: (body, response) =>
            new ContributionConfirmError(body.code ?? '', response.status, body.error ?? 'Failed to uncheck contribution', staleVersion(body)),
        }),
    },
    patch: markPendingReview,
    invalidateKeys: invalidateContributionStatusFlip,
  });
}

// Standalone Pending → Confirmed flip (USI-006). Flips the row's green check the
// instant it's clicked — without the optimistic patch the check appeared only
// after the POST AND a full sort-inbox refetch (two round-trips that read as a
// dead button). Status-only flip, so contacts.all is deliberately omitted —
// distinct from useVoidContribution, which may shift aggregates.
export function useConfirmContribution() {
  return useOptimisticStatusFlip({
    endpoint: {
      entity: 'Contribution',
      send: (id, concurrencyVersion) =>
        apiJson<ContributionDto>(`/api/finance/contributions/${id}/confirm`, {
          method: 'POST',
          json: { concurrencyVersion },
          error: (body, response) =>
            new ContributionConfirmError(body.code ?? '', response.status, body.error ?? 'Failed to confirm contribution', staleVersion(body)),
        }),
    },
    patch: markConfirmed,
    invalidateKeys: invalidateContributionStatusFlip,
  });
}

// AL EDI delta-export (design §6): the manual one-click "mark as reported"
// carve-out. A treasurer who entered a contribution directly in the AL FCPA
// portal flips it Confirmed → Reported so our rules see it WITHOUT us ever
// re-exporting it (a re-export would duplicate it on the append-only portal —
// ADR-020). An audited row mutation, not an ExportBatch. The optional `note`
// (e.g. "entered directly in the AL FCPA portal") rides the request; an empty
// note is dropped by JSON.stringify, so the wire omits it. Reuses the
// `ContributionConfirmError` envelope (code/status/message transport) — the wire
// codes come from `ContributionMarkReportedErrorCodes`.
export function useMarkContributionReported() {
  return useOptimisticStatusFlip({
    endpoint: {
      entity: 'Contribution',
      send: (id, concurrencyVersion, { note }: { note?: string }) =>
        apiJson<ContributionDto>(`/api/finance/contributions/${id}/mark-reported`, {
          method: 'POST',
          json: { concurrencyVersion, note },
          error: (body, response) =>
            new ContributionConfirmError(body.code ?? '', response.status, body.error ?? 'Failed to mark this contribution as reported', staleVersion(body)),
        }),
    },
    patch: markReported,
    invalidateKeys: invalidateContributionStatusFlip,
  });
}

// AL EDI delta-export (design §6): reverse a manual mark
// (Reported → Confirmed), returning the row to the export pool. The audited,
// provenance-gated carve-out of ADR-007 — the backend allows it only when the
// row has no export-upload membership.
export function useUnmarkContributionReported() {
  return useOptimisticStatusFlip({
    endpoint: {
      entity: 'Contribution',
      send: (id, concurrencyVersion) =>
        apiJson<ContributionDto>(`/api/finance/contributions/${id}/unmark-reported`, {
          method: 'POST',
          json: { concurrencyVersion },
          error: (body, response) =>
            new ContributionConfirmError(body.code ?? '', response.status, body.error ?? 'Failed to undo the mark-reported', staleVersion(body)),
        }),
    },
    patch: markUnreported,
    invalidateKeys: invalidateContributionStatusFlip,
  });
}

// The triage-time "treasurer asked the donor for occupation / employer; awaiting response"
// hook. POSTs to `/api/finance/contributions/{id}/donor-info-requested` with the channel the
// request went out on, and gets back the updated contribution. The gift's LedgerStatus is
// unchanged; the concurrency token is not, which is why the response is read rather than
// discarded — a double-click re-sends the spent token and conflicts.
//
// Distinct shape from `useConfirmContribution`: the endpoint returns its
// own `DonorInfoRequestErrorCodes` namespace
// (`ContributionNotFound` → 404, `ConcurrencyConflict` → 409) — not the
// USI-006 `ContributionConfirmErrorCodes` family. Surfacing it as a
// typed error class keeps the triage modal's 409 messaging structurally
// distinct from the confirm-flow's and avoids muddling the two
// error-code namespaces.
export class DonorInfoRequestedError extends Error {
  code: string;
  status: number;
  constructor(
    code: string,
    status: number,
    message: string,
    /** The row's version on a 409 stale write, read by `useRowWrite`. */
    readonly currentVersion: number | null = null,
  ) {
    super(message);
    this.name = 'DonorInfoRequestedError';
    this.code = code;
    this.status = status;
  }
}

export function useDonorInfoRequested() {
  const queryClient = useQueryClient();

  return useRowWrite<'Contribution', RowVersion, { channel: DonorInfoRequestChannel }, ContributionDto>(
    {
      entity: 'Contribution',
      send: (id, concurrencyVersion, { channel }) =>
        apiJson<ContributionDto>(`/api/finance/contributions/${id}/donor-info-requested`, {
          method: 'POST',
          json: { concurrencyVersion, channel },
          error: (body, response) =>
            new DonorInfoRequestedError(
              body.code ?? '',
              response.status,
              body.error ?? 'Failed to record donor info request',
              staleVersion(body),
            ),
        }),
    },
    {
      onSuccess: (data, { row }) => {
        // The row writes read the new token from the version store; this keeps it in the cached
        // row for the writes that still read it from there (contributor shape, the resolution
        // dialog), since a rapid follow-up's onMutate cancels the settle refetch.
        reconcileSortInboxRowVersion(queryClient, contributionRow(row.id), data?.concurrencyVersion);
        // The request history for this gift just gained a row.
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.donorInfoRequests(row.id) });
        // Recording a request does not flip LedgerStatus, so the staged ledger and the
        // earliest-staged-date default don't refetch. The detail key does, so the audit-history
        // card on StagedDetailPanel shows the new row.
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.detail(row.id) });
        // The load-bearing invalidation: the refetch parks the row out of the inbox queue until
        // the donor info is filled. `contacts.all` is not invalidated: the attestation changes no
        // contact aggregate.
        queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
      },
    },
  );
}

/**
 * The recorded "I asked this donor" history for one gift, most recent first — what the
 * treasurer reads before deciding whether to chase again. Enabled only when a contribution id
 * is supplied, so the panel that renders it can mount before a row is selected.
 */
export function useDonorInfoRequests(contributionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.finance.contributions.donorInfoRequests(contributionId ?? ''),
    enabled: !!contributionId,
    queryFn: async (): Promise<DonorInfoRequestDto[]> => {
      const response = await apiResponse(`/api/finance/contributions/${contributionId}/donor-info-request-history`);
      if (!response.ok) {
        throw new ApiError(
          'Failed to load donor info request history', response.status, false);
      }
      return response.json();
    },
  });
}

// USI-004 / ADR-011: useKeepAsConfirmed deleted with the adverse-status
// banner machinery.

export function useRedesignateContribution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contributionId, ...data }: RedesignateRequest & { contributionId: string }) => {
      const response = await apiResponse(`/api/finance/contributions/${contributionId}/redesignate`, {
        method: 'POST',
        json: data,
      });
      if (response.status === 409) {
        throw new Error(CLIENT_CONFLICT_MARKER);
      }
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to redesignate contribution');
      }
      return response.json() as Promise<ResolutionResult>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
    },
  });
}
